import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function SecurityPage() {
  return (
    <>
      <Navbar />
      <main>
        <header className="pt-24 md:pt-28 pb-8 md:pb-12 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="flex items-center gap-2 text-sm text-foreground-500 mb-4">
              <Link to="/" className="hover:text-primary-600 transition-colors">Home</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-400">Security &amp; Privacy</span>
            </div>
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold mb-4">
              Trust Center
            </span>
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 leading-tight mb-4">
              Security &amp; Privacy Center
            </h1>
            <p className="text-sm md:text-base text-foreground-600 leading-relaxed max-w-2xl">
              Your safety and privacy are our top priorities. This page explains how Vigorbuy Spreadsheet protects your information and provides transparent guidance for secure browsing.
            </p>
          </div>
        </header>

        <div className="relative w-full h-[240px] md:h-[320px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Clean%20minimalist%20security%20concept%20with%20shield%20icon%20symbol%20on%20soft%20cream%20background%2C%20warm%20natural%20lighting%2C%20modern%20editorial%20style%2C%20trustworthy%20atmosphere%2C%20abstract%20geometric%20shapes%20representing%20protection%2C%20neutral%20beige%20and%20soft%20sage%20green%20tones%2C%20professional%20privacy%20aesthetic&width=1400&height=450&seq=vigorbuy-security-hero&orientation=landscape"
            alt="Vigorbuy Spreadsheet Security Center"
            title="Vigorbuy Spreadsheet Security & Privacy"
            className="w-full h-full object-cover"
          />
        </div>

        <div className="max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-14">
          <div className="prose-content">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4">
              Our Commitment to Your Safety
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
              Vigorbuy Spreadsheet is designed with a privacy-first philosophy. We believe that product research and browsing should be completely anonymous and secure. Unlike many online platforms, we do not collect, store, or process any personal information from our visitors.
            </p>

            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4">
              No Personal Data Collection
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
              <strong>Vigorbuy Spreadsheet does not collect any personal data.</strong> You are never asked to create an account, provide an email address, submit a phone number, or share any identifying information. Your browsing activity on our platform is completely anonymous by design.
            </p>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
              This zero-data-collection policy means there is no personal information to leak, sell, or misuse. Even if a security incident were to occur, there would be no user data at risk because we simply do not have any. This approach represents the strongest possible privacy protection for our visitors.
            </p>

            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4">
              No Payment Processing
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
              Vigorbuy Spreadsheet is an informational platform only. We do not process payments, handle credit card transactions, or store any financial information. All purchases are completed on the official product pages of third-party websites after you click through from our spreadsheet. This separation between product discovery and financial transactions provides an additional layer of security for our users.
            </p>

            <div className="bg-background-100 rounded-xl p-6 md:p-8 my-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-100">
                  <i className="ri-shield-check-line text-accent-600 text-lg"></i>
                </div>
                <h3 className="font-heading font-semibold text-lg text-foreground-900">Safety Summary</h3>
              </div>
              <ul className="space-y-2.5">
                <li className="flex items-start gap-2.5 text-sm text-foreground-700">
                  <i className="ri-check-line text-accent-600 mt-0.5"></i>
                  No account registration required — browse anonymously
                </li>
                <li className="flex items-start gap-2.5 text-sm text-foreground-700">
                  <i className="ri-check-line text-accent-600 mt-0.5"></i>
                  No personal data collection of any kind
                </li>
                <li className="flex items-start gap-2.5 text-sm text-foreground-700">
                  <i className="ri-check-line text-accent-600 mt-0.5"></i>
                  No payment processing or financial data storage
                </li>
                <li className="flex items-start gap-2.5 text-sm text-foreground-700">
                  <i className="ri-check-line text-accent-600 mt-0.5"></i>
                  Product links verified before being added to listings
                </li>
                <li className="flex items-start gap-2.5 text-sm text-foreground-700">
                  <i className="ri-check-line text-accent-600 mt-0.5"></i>
                  All external links open securely with privacy protection
                </li>
              </ul>
            </div>

            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4">
              External Link Disclaimer
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
              Vigorbuy Spreadsheet contains links to external third-party websites, including product pages and e-commerce platforms. These external sites have their own privacy policies, terms of service, and security practices. We are not responsible for the content, security, or privacy practices of any third-party websites.
            </p>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
              We recommend that you always verify you are on the correct, official domain before entering any personal or payment information during checkout. Look for the lock icon (HTTPS) in your browser address bar and confirm the domain matches the expected e-commerce platform.
            </p>

            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4">
              Product Information Accuracy
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
              While we strive to maintain accurate and current product information in our spreadsheet listings, product details — including prices, availability, and specifications — can change without notice. Vigorbuy Spreadsheet serves as a product discovery and comparison tool. Always verify critical product information on the official product page before completing any purchase.
            </p>

            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4">
              Secure Browsing Recommendations
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
              For the safest possible browsing experience, we recommend the following general practices when shopping online:
            </p>
            <ul className="space-y-2.5 mb-6 pl-0">
              <li className="flex items-start gap-2.5 text-sm text-foreground-700">
                <i className="ri-arrow-right-s-line text-accent-600 mt-0.5"></i>
                Keep your web browser and operating system updated to the latest version
              </li>
              <li className="flex items-start gap-2.5 text-sm text-foreground-700">
                <i className="ri-arrow-right-s-line text-accent-600 mt-0.5"></i>
                Use strong, unique passwords for any shopping accounts you create
              </li>
              <li className="flex items-start gap-2.5 text-sm text-foreground-700">
                <i className="ri-arrow-right-s-line text-accent-600 mt-0.5"></i>
                Enable two-factor authentication when available on shopping platforms
              </li>
              <li className="flex items-start gap-2.5 text-sm text-foreground-700">
                <i className="ri-arrow-right-s-line text-accent-600 mt-0.5"></i>
                Avoid entering payment information on public or shared Wi-Fi networks
              </li>
              <li className="flex items-start gap-2.5 text-sm text-foreground-700">
                <i className="ri-arrow-right-s-line text-accent-600 mt-0.5"></i>
                Verify that product URLs match the official domain before purchasing
              </li>
              <li className="flex items-start gap-2.5 text-sm text-foreground-700">
                <i className="ri-arrow-right-s-line text-accent-600 mt-0.5"></i>
                Review order confirmations and receipts carefully after each purchase
              </li>
            </ul>

            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4">
              Independent Platform Statement
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
              Vigorbuy Spreadsheet is an independent informational resource. We are not affiliated with, endorsed by, or officially connected to any of the e-commerce platforms or product brands listed in our spreadsheet. Our platform exists solely as a free organizational tool designed to help shoppers discover and compare products more efficiently.
            </p>

            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4">
              Contact Us
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-4">
              If you have questions about the security or privacy practices of Vigorbuy Spreadsheet, or if you notice any issues with product listings, please reach out through our main website. We are committed to maintaining a safe, transparent, and trustworthy platform for all shoppers.
            </p>

            <div className="flex flex-col sm:flex-row items-center gap-4 mt-10 p-6 rounded-xl bg-primary-50 border border-primary-100">
              <div className="flex-1">
                <p className="font-heading font-semibold text-foreground-900 mb-1">Have a security concern?</p>
                <p className="text-sm text-foreground-600">Visit the main website for support and more information about our platform.</p>
              </div>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-semibold bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                Visit Main Website
                <i className="ri-arrow-right-up-line"></i>
              </a>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-background-200/70">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Homepage
            </Link>
          </div>
        </div>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'WebPage',
              name: 'Security & Privacy Center - Vigorbuy Spreadsheet Guide',
              description: 'Your safety and privacy are our top priorities. Learn how Vigorbuy Spreadsheet protects your information with zero data collection, no payment processing, and secure browsing recommendations.',
              publisher: {
                '@type': 'Organization',
                name: 'Vigorbuy Spreadsheet Guide',
              },
            }),
          }}
        />
      </main>
      <Footer />
    </>
  );
}